import { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { legalService } from '@/services/legal';

interface PendingDocument {
  type: string;
  version: string;
  url: string;
  title: string;
}

interface LegalAcceptanceGateProps {
  pendingDocuments: PendingDocument[];
  onAccepted: () => void;
}

/**
 * Full-screen legal acceptance gate.
 * Rendered by ProtectedRoute INSTEAD OF child routes when
 * legal acceptance is pending. Dashboard components never mount.
 */
export const LegalAcceptanceGate = ({ pendingDocuments, onAccepted }: LegalAcceptanceGateProps) => {
  const [checkedDocs, setCheckedDocs] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allChecked =
    pendingDocuments.length > 0 &&
    pendingDocuments.every((doc) => checkedDocs[doc.type]);

  const toggleDoc = (type: string) => {
    setCheckedDocs((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const handleAccept = async () => {
    if (!allChecked || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const documentTypes = pendingDocuments.map((d) => d.type);
      const result = await legalService.acceptDocuments(documentTypes);

      // Update tokens in localStorage
      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      // Clear the legal acceptance state and reload
      onAccepted();
      window.location.reload();
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Failed to accept legal documents. Please try again.';
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={styles.card}
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.iconWrapper}>
            <Shield size={32} color="#1e4a7a" />
          </div>
          <h2 style={styles.heading}>Legal Agreement Required</h2>
          <p style={styles.subtitle}>
            To continue using Amplify, please review and accept the following
            legal documents.
          </p>
        </div>

        {/* Document list */}
        <div style={styles.docList}>
          {pendingDocuments.map((doc) => (
            <label key={doc.type} style={styles.docRow}>
              <input
                type="checkbox"
                checked={!!checkedDocs[doc.type]}
                onChange={() => toggleDoc(doc.type)}
                disabled={submitting}
                style={styles.checkbox}
              />
              <span style={styles.docLabel}>
                I have read and agree to the{' '}
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  {doc.title}
                </a>
              </span>
            </label>
          ))}
        </div>

        {/* Error message */}
        {error && <p style={styles.error}>{error}</p>}

        {/* Accept button */}
        <button
          onClick={handleAccept}
          disabled={!allChecked || submitting}
          style={{
            ...styles.button,
            ...((!allChecked || submitting) ? styles.buttonDisabled : {}),
          }}
        >
          {submitting ? 'Submitting...' : 'Accept All'}
        </button>
      </motion.div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(10, 31, 68, 0.85)',
    padding: '1rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: '2.5rem',
    maxWidth: 560,
    width: '100%',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.25)',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '1.5rem',
  },
  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: 'rgba(30, 74, 122, 0.08)',
    marginBottom: '1rem',
  },
  heading: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#0a1f44',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    color: '#5a6a7e',
    margin: 0,
    lineHeight: 1.5,
  },
  docList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  docRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    color: '#1a2b45',
    lineHeight: 1.5,
  },
  checkbox: {
    accentColor: '#1e4a7a',
    width: 18,
    height: 18,
    marginTop: 2,
    flexShrink: 0,
    cursor: 'pointer',
  },
  docLabel: {
    flex: 1,
  },
  link: {
    color: '#1e4a7a',
    textDecoration: 'underline',
    fontWeight: 500,
  },
  error: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.875rem',
    color: '#d32f2f',
    textAlign: 'center' as const,
    margin: '0 0 1rem 0',
  },
  button: {
    width: '100%',
    padding: '0.875rem 1.5rem',
    border: 'none',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #1e4a7a 0%, #16a085 100%)',
    color: '#ffffff',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};
